/**
 * JobShellsSeed · iter303ak — Quote Sent · Task 5
 * ────────────────────────────────────────────────
 * Pre-seeds the Accepted-status data shells so the next status has
 * ready-to-fill scaffolding:
 *   - appraisal.job_tracking → empty dates for each milestone (Legacy:
 *     AcceptedJobCheckListRpt "Appraisal Tracking Sheet")
 *   - appraisal.work_sheet → roof snapshot + empty work_history[]
 *     (Legacy: JobDetailsQueryReport)
 *   - appraisal.job_status_checklist → materials list derived from
 *     any built quotes (Legacy: JobStatusCheckListReport)
 *
 * Idempotent: if job_tracking already exists on the appraisal, the seed
 * only backfills MISSING keys. Materials list is refreshed on every
 * click.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Layers, CheckCircle2, RefreshCw, AlertCircle, FileText, ClipboardList, Truck } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const EMPTY_TRACKING = {
  acceptance: { done: false, at: null },
  deposit: { done: false, at: null, amount: null },
  credit_check: { done: false, at: null },
  materials_ordered: { done: false, at: null },
  cust_notified: { done: false, at: null },
  work_started: { done: false, at: null },
  work_completed: { done: false, at: null },
  extra_labour: { done: false, at: null },
  extra_materials: { done: false, at: null },
  invoiced: { done: false, at: null },
  paid: { done: false, at: null },
  followup: { done: false, at: null },
};

export default function JobShellsSeed({ action, capturedData, onNext, onBack, onCancel, canBack, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const property = capturedData?.property_record || {};
  const roofDetails = capturedData?.roof_details_record || {};
  const primaryTypes = capturedData?.appraisal?.primary_quote_types
    || capturedData?.appraisal_record?.primary_quote_types
    || [];
  const buildStatus = capturedData?.appraisal?.quote_build_status || {};

  const [seeded, setSeeded] = useState(false);
  const [seededAt, setSeededAt] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    // Check if shells already exist on the appraisal.
    if (!appraisalId) return;
    (async () => {
      try {
        const res = await api.get(`/appraisals/${appraisalId}`);
        const app = res.data;
        if (app?.job_shells_seeded_at) {
          setSeeded(true);
          setSeededAt(app.job_shells_seeded_at);
        }
        setPreview({
          job_tracking: app?.job_tracking || EMPTY_TRACKING,
          work_sheet: app?.work_sheet || null,
          job_status_checklist: app?.job_status_checklist || null,
        });
      } catch (e) {
        // Non-fatal — user can still seed.
      }
    })();
  }, [appraisalId]);

  const buildShells = () => {
    // work_sheet snapshot from current roof data.
    const workSheet = {
      snapshot_at: new Date().toISOString(),
      roof_type: roofDetails.roof_type || property.roof_type || '',
      material_type: roofDetails.material_type || property.material_type || '',
      profile: roofDetails.profile || '',
      colour: roofDetails.roof_colour || property.roof_colour || '',
      pitch: roofDetails.roof_pitch || property.roof_pitch || '',
      storeys: roofDetails.roof_stories || property.roof_stories || '',
      floor_m2: roofDetails.floor_m2 || null,
      roof_m2: roofDetails.roof_m2 || null,
      ridge_mtr: roofDetails.ridge_mtr || null,
      work_description: '', // filled at Accepted
      mats_received: false,
      booked_date: null,
      work_history: [],
    };
    // job_status_checklist — one item per primary quote type + status flag.
    const items = primaryTypes.map(ref => ({
      quote_type: ref,
      built: buildStatus[ref]?.built === true,
      description: `${ref} — materials & labour to be finalised at Accepted`,
      quantity: null,
      unit: null,
    }));
    return { job_tracking: EMPTY_TRACKING, work_sheet: workSheet, job_status_checklist: { items, generated_at: new Date().toISOString(), generated_from_quotes: true } };
  };

  const runSeed = async () => {
    if (!appraisalId) { setErr('No appraisal linked — cannot seed.'); return; }
    setSeeding(true); setErr('');
    try {
      const shells = buildShells();
      // Merge job_tracking so we don't wipe partial data if an operator
      // is re-seeding (e.g. rolled back from Accepted).
      const existing = preview?.job_tracking || {};
      const merged_tracking = { ...shells.job_tracking, ...existing };
      const payload = {
        job_tracking: merged_tracking,
        work_sheet: shells.work_sheet,
        job_status_checklist: shells.job_status_checklist,
      };
      const res = await api.patch(`/appraisals/${appraisalId}`, payload);
      const nowIso = new Date().toISOString();
      // Best-effort second PATCH to timestamp when seeding ran.
      await api.patch(`/appraisals/${appraisalId}`, { job_shells_seeded_at: nowIso }).catch(() => {});
      setSeeded(true);
      setSeededAt(nowIso);
      setPreview({
        job_tracking: res.data?.job_tracking || merged_tracking,
        work_sheet: res.data?.work_sheet || shells.work_sheet,
        job_status_checklist: res.data?.job_status_checklist || shells.job_status_checklist,
      });
      if (onChange) onChange(nowIso);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="wf-job-shells-seed">
      <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-4">
        <div className="flex items-start gap-3">
          <Layers className="w-5 h-5 text-purple-700 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-purple-900">Pre-seed Accepted Shells</div>
            <div className="text-sm text-purple-800 mt-1">{params.prompt || 'Pre-fill the Accepted-status data shells (job tracking, work sheet, materials list) so the Accepted workflow has ready scaffolding.'}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-3 rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-2">
            <FileText className="w-4 h-4 text-slate-500" /> Job Tracking (dates)
          </div>
          <div className="text-xs text-slate-500 space-y-0.5">
            <div>12 milestone slots (deposit, materials, start, complete, invoice, paid, follow-up…)</div>
            <div>Existing dates preserved on re-seed.</div>
          </div>
        </div>
        <div className="p-3 rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-2">
            <ClipboardList className="w-4 h-4 text-slate-500" /> Work Sheet (snapshot)
          </div>
          <div className="text-xs text-slate-500 space-y-0.5">
            <div>Roof spec snapshot (type/brand/profile/pitch/m²)</div>
            <div>Empty work_history[] for on-site logging</div>
          </div>
        </div>
        <div className="p-3 rounded-lg border border-slate-200 bg-white">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800 mb-2">
            <Truck className="w-4 h-4 text-slate-500" /> Materials Checklist
          </div>
          <div className="text-xs text-slate-500 space-y-0.5">
            <div>{primaryTypes.length || 0} item{primaryTypes.length === 1 ? '' : 's'} from built quotes</div>
            <div>Regenerates on every re-seed</div>
          </div>
        </div>
      </div>

      {seeded && seededAt ? (
        <div className="flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="wf-shells-seeded-banner">
          <CheckCircle2 className="w-4 h-4" />
          <span>Shells seeded at <span className="font-medium">{new Date(seededAt).toLocaleString()}</span>. Click again to refresh the materials list.</span>
        </div>
      ) : null}

      {err ? (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <button
          onClick={runSeed}
          disabled={seeding || !appraisalId}
          className="px-4 py-1.5 rounded-md text-sm bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40 inline-flex items-center gap-2"
          data-testid="wf-shells-seed-run"
        >
          <RefreshCw className={`w-4 h-4 ${seeding ? 'animate-spin' : ''}`} />
          {seeding ? 'Seeding…' : (seeded ? 'Re-seed Shells' : 'Seed Shells Now')}
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-shells-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel ? (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-shells-cancel">Cancel</button>
          ) : null}
          <button
            onClick={onNext}
            disabled={!seeded}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-shells-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
